import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import { Responsive, WidthProvider, Layout } from 'react-grid-layout';
import { RotateCcw } from 'lucide-react';
import {
  MapCard,
  ModelsCard,
  ChartCard,
  HeatmapCard,
  SettingsMenu,
  TimezoneDisplay,
  VariableSelector,
  WeightTypeSelector,
  ForecastHorizonSelector,
  ZoneSelector,
} from './index';
import { CardConfig } from './SettingsMenu';
import { GRID_CONFIG } from '../constants/layoutConfigs';
import { GRID_STYLES } from '../constants/gridStyles';
import { getFilteredLayouts, filterSavedLayouts, isCardEnabled } from '../utils/layoutUtils';
import { TabState, DashboardTab } from '../types/tabs.types';
import { VariableType } from '../types/dashboard.types';
import type { MarketZoneId } from '@app/common/static-data/energy-markets';
import { MarketAggregatesProvider } from '../contexts/MarketAggregatesContext';
import { useMarketAggregates } from '../contexts/MarketAggregatesContext';

// Helper function to determine the correct weightType based on selectedVariable
const getWeightTypeForVariable = (
  selectedVariable: VariableType,
): 'population' | 'wind_capacity' | 'solar_capacity' => {
  switch (selectedVariable) {
    case 'temperature':
      return 'population';
    case 'wind':
      return 'wind_capacity';
    case 'solar':
      return 'solar_capacity';
    default:
      return 'wind_capacity'; // fallback
  }
};

const ResponsiveGridLayout = WidthProvider(Responsive);

interface DashboardTabContentProps {
  tabId: string;
  tabState: TabState;
  tab: DashboardTab;
  isActive: boolean;
  onStateChange: (tabId: string, updates: Partial<TabState>) => void;
}

const DashboardTabContent: React.FC<DashboardTabContentProps> = memo(
  ({ tabId, tabState, isActive, onStateChange }) => {
    // Local state initialized once from tab state - no syncing on active change
    const [cardConfigs, setCardConfigs] = useState<CardConfig[]>(() => tabState.cardConfigs);
    const [layouts, setLayouts] = useState(() => tabState.layouts);

    const [selectedVariable, setSelectedVariable] = useState<VariableType>(
      () => tabState.selectedVariable || 'wind',
    );
    const [selectedZone, setSelectedZone] = useState<MarketZoneId | null>(
      () => tabState.selectedZone,
    );
    const [selectedZones, setSelectedZones] = useState<MarketZoneId[]>(
      () => tabState.selectedZones || (tabState.selectedZone ? [tabState.selectedZone] : []),
    );

    // Track initialization to avoid unnecessary updates
    const initializationRef = useRef(false);
    // Track when card configs were last updated locally to avoid sync conflicts
    const lastCardConfigUpdateRef = useRef(Date.now());
    // Track recent local updates to avoid external sync overrides (no longer needed)

    // Refs for auto-scrolling functionality
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const isResizingRef = useRef(false);
    const autoScrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const mousePositionRef = useRef({ x: 0, y: 0 });

    // Only sync state if the tabState has been externally updated (not just active state change)
    const lastTabStateRef = useRef(tabState);
    useEffect(() => {
      // Check if tabState actually changed (not just the reference)
      const hasStateChanged = JSON.stringify(lastTabStateRef.current) !== JSON.stringify(tabState);

      if (hasStateChanged && initializationRef.current) {
        // Only sync card configs if they weren't recently updated locally (prevent override conflicts)
        const timeSinceLastCardUpdate = Date.now() - lastCardConfigUpdateRef.current;
        const shouldSyncCardConfigs = timeSinceLastCardUpdate > 500; // 500ms buffer

        if (shouldSyncCardConfigs) {
          setCardConfigs(tabState.cardConfigs);
        }
        setLayouts(tabState.layouts);
        // Do not sync selectedVariable/selectedZone from persisted state post-initialization.
        // Local state is the source of truth while this tab is active; persistence is write-only here.
      }

      if (!initializationRef.current) {
        initializationRef.current = true;
      }

      lastTabStateRef.current = tabState;
    }, [tabState]);

    // Debounced state updates to avoid excessive saves
    const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const debouncedStateUpdate = useCallback(
      (updates: Partial<TabState>) => {
        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }

        updateTimeoutRef.current = setTimeout(() => {
          onStateChange(tabId, updates);
        }, 300);
      },
      [onStateChange, tabId],
    );

    const handleLayoutChange = useCallback(
      (_layout: Layout[], allLayouts: any) => {
        setLayouts(allLayouts);
        debouncedStateUpdate({ layouts: allLayouts });
      },
      [debouncedStateUpdate],
    );

    const resetLayout = useCallback(() => {
      const defaultLayouts = getFilteredLayouts(cardConfigs);
      setLayouts(defaultLayouts);
      onStateChange(tabId, { layouts: defaultLayouts });
    }, [cardConfigs, onStateChange, tabId]);

    const handleCardConfigChange = useCallback(
      (newConfigs: CardConfig[]) => {
        // Track that we're updating card configs locally
        lastCardConfigUpdateRef.current = Date.now();

        setCardConfigs(newConfigs);

        // Reset layout when cards change to avoid layout issues
        // Make this immediate to prevent timing conflicts with debounced updates
        const filteredLayouts = getFilteredLayouts(newConfigs);
        setLayouts(filteredLayouts);
        onStateChange(tabId, {
          cardConfigs: newConfigs,
          layouts: filteredLayouts,
        });
      },
      [onStateChange, tabId],
    );

    const handleZonesChange = useCallback(
      (zones: MarketZoneId[]) => {
        setSelectedZones(zones);
        const primary = zones[0] ?? null;
        setSelectedZone(primary);
        debouncedStateUpdate({ selectedZones: zones, selectedZone: primary });
      },
      [debouncedStateUpdate],
    );

    const handleVariableChange = useCallback(
      (variable: VariableType) => {
        setSelectedVariable(variable);
        // Save immediately for variable changes - no debounce needed
        onStateChange(tabId, { selectedVariable: variable });
      },
      [onStateChange, tabId],
    );

    const handleCardNameChange = useCallback(
      (cardId: string, newName: string) => {
        const updatedConfigs = cardConfigs.map((config) =>
          config.id === cardId ? { ...config, name: newName } : config,
        );
        lastCardConfigUpdateRef.current = Date.now();
        setCardConfigs(updatedConfigs);
        onStateChange(tabId, { cardConfigs: updatedConfigs });
      },
      [cardConfigs, onStateChange, tabId],
    );

    // Update layout when card configs change (filter out disabled cards)
    useEffect(() => {
      const enabledCardIds = cardConfigs
        .filter((config) => config.enabled)
        .map((config) => config.id);
      const filteredLayouts = filterSavedLayouts(layouts, enabledCardIds);

      // Only update if there's a meaningful change
      if (JSON.stringify(filteredLayouts) !== JSON.stringify(layouts)) {
        setLayouts(filteredLayouts);
      }
    }, [cardConfigs]);

    // Track mouse position during resize
    useEffect(() => {
      const handleMouseMove = (e: MouseEvent) => {
        mousePositionRef.current = { x: e.clientX, y: e.clientY };
      };

      if (isActive) {
        document.addEventListener('mousemove', handleMouseMove);
        return () => document.removeEventListener('mousemove', handleMouseMove);
      }
    }, [isActive]);

    // Auto-scrolling logic when resizing cards
    const startAutoScroll = useCallback(() => {
      if (autoScrollIntervalRef.current) return; // Already scrolling

      autoScrollIntervalRef.current = setInterval(() => {
        if (!isResizingRef.current || !scrollContainerRef.current) {
          // Stop scrolling if no longer resizing
          if (autoScrollIntervalRef.current) {
            clearInterval(autoScrollIntervalRef.current);
            autoScrollIntervalRef.current = null;
          }
          return;
        }

        const container = scrollContainerRef.current;
        const containerRect = container.getBoundingClientRect();
        const mouseY = mousePositionRef.current.y;

        // Calculate distance from bottom of container
        const distanceFromBottom = containerRect.bottom - mouseY;
        const scrollThreshold = 100; // Start scrolling when within 100px of bottom

        if (distanceFromBottom < scrollThreshold && distanceFromBottom > 0) {
          // Calculate scroll speed based on distance from bottom (closer = faster)
          const scrollSpeed = Math.max(2, (scrollThreshold - distanceFromBottom) / 8);
          container.scrollTop += scrollSpeed;
        }
      }, 16); // ~60fps
    }, []);

    const stopAutoScroll = useCallback(() => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }
    }, []);

    // Handle grid resize events
    const handleResizeStart = useCallback(() => {
      isResizingRef.current = true;
      startAutoScroll();
    }, [startAutoScroll]);

    const handleResizeStop = useCallback(() => {
      isResizingRef.current = false;
      stopAutoScroll();
    }, [stopAutoScroll]);

    const handleResize = useCallback(() => {
      // During resize, check if we need to start/continue auto-scrolling
      if (isResizingRef.current) {
        startAutoScroll();
      }
    }, [startAutoScroll]);

    // Clean up timeout on unmount
    useEffect(() => {
      return () => {
        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }
        stopAutoScroll();
      };
    }, [stopAutoScroll]);

    // Persist context selections (models, runs, weightType, maxLeadTimeHours) into tab state so they survive reloads
    const { state: contextState } = useMarketAggregates();
    const lastPersistedContextRef = useRef<string | null>(null);

    useEffect(() => {
      // Only persist the minimal slice necessary for restoration
      const persistedContextState = {
        weightType: contextState.weightType,
        maxLeadTimeHours: contextState.maxLeadTimeHours,
        availableModels: contextState.availableModels.map((model) => ({
          id: model.id,
          label: model.label,
          isSelected: model.isSelected,
          selectedRuns: model.selectedRuns,
        })),
      } as const;

      // Only save if the context state actually changed
      const currentStateString = JSON.stringify(persistedContextState);
      if (lastPersistedContextRef.current !== currentStateString) {
        lastPersistedContextRef.current = currentStateString;
        // Debounce writes to localStorage-backed tab state
        debouncedStateUpdate({ contextState: persistedContextState as any });
      }
    }, [
      contextState.weightType,
      contextState.maxLeadTimeHours,
      contextState.availableModels,
      debouncedStateUpdate,
    ]);

    // Flush full tab state when deactivating this tab (ensures persistence before switching away)
    const wasActiveRef = useRef<boolean>(isActive);
    useEffect(() => {
      if (wasActiveRef.current && !isActive) {
        const persistedContextState = {
          weightType: contextState.weightType,
          maxLeadTimeHours: contextState.maxLeadTimeHours,
          availableModels: contextState.availableModels.map((model) => ({
            id: model.id,
            label: model.label,
            isSelected: model.isSelected,
            selectedRuns: model.selectedRuns,
          })),
        } as const;

        // Immediate save to avoid losing changes on quick switches/reloads
        onStateChange(tabId, {
          cardConfigs,
          layouts,
          selectedVariable,
          selectedZone,
          contextState: persistedContextState as any,
        });
      }
      wasActiveRef.current = isActive;
    }, [
      isActive,
      tabId,
      onStateChange,
      cardConfigs,
      layouts,
      selectedVariable,
      selectedZone,
      contextState.weightType,
      contextState.availableModels,
      contextState.maxLeadTimeHours,
    ]);

    return (
      <div
        className="h-full flex flex-col bg-white dark:bg-gray-900 absolute inset-0"
        style={{
          visibility: isActive ? 'visible' : 'hidden',
          zIndex: isActive ? 1 : 0,
          pointerEvents: isActive ? 'auto' : 'none',
        }}
      >
        <style>{GRID_STYLES}</style>

        {/* Header with Controls */}
        <div className="flex-shrink-0 px-4 pt-4 mb-2">
          <div className="flex flex-wrap items-center justify-between gap-3 md:gap-4">
            <div className="flex flex-wrap items-center gap-3 md:gap-4">
              <VariableSelector
                selectedVariable={selectedVariable}
                onSelectionChange={handleVariableChange}
              />
              <WeightTypeSelector selectedVariable={selectedVariable} />
              <ForecastHorizonSelector />
              <ZoneSelector selectedZones={selectedZones} onZonesChange={handleZonesChange} />
              <TimezoneDisplay />
            </div>
            <div className="flex items-center gap-3 md:gap-4">
              <SettingsMenu cardConfigs={cardConfigs} onCardConfigChange={handleCardConfigChange} />
              <button
                onClick={resetLayout}
                className="flex items-center gap-2 px-3 py-1 text-xs bg-gray-100 dark:bg-gray-800 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 whitespace-nowrap"
                title="Reset Layout"
              >
                <RotateCcw className="h-3 w-3" />
                Reset Layout
              </button>
            </div>
          </div>
        </div>

        {/* Main content area with proper viewport fitting */}
        <div className="flex flex-1 min-h-0 relative w-full">
          <div className="flex flex-col min-h-0 relative w-full">
            <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-4 pt-4 pb-4">
              <ResponsiveGridLayout
                className="layout"
                layouts={layouts}
                onLayoutChange={handleLayoutChange}
                onResizeStart={handleResizeStart}
                onResize={handleResize}
                onResizeStop={handleResizeStop}
                {...GRID_CONFIG}
              >
                {isCardEnabled(cardConfigs, 'map') && (
                  <div key="map">
                    <MapCard selectedZones={selectedZones} onZonesChange={handleZonesChange} />
                  </div>
                )}
                {isCardEnabled(cardConfigs, 'models') && (
                  <div key="models">
                    <ModelsCard tabId={tabId} />
                  </div>
                )}
                {cardConfigs
                  .filter(
                    (config) =>
                      config.enabled && (config.type === 'chart' || config.id === 'chart'),
                  )
                  .map((config) => (
                    <div key={config.id}>
                      <ChartCard
                        selectedVariable={selectedVariable}
                        selectedZone={selectedZones.length > 0 ? selectedZones : null}
                        tabId={tabId}
                        cardId={config.id}
                        cardName={config.name}
                        onCardNameChange={handleCardNameChange}
                      />
                    </div>
                  ))}
                {isCardEnabled(cardConfigs, 'heatmap') && (
                  <div key="heatmap">
                    <HeatmapCard tabId={tabId} />
                  </div>
                )}
              </ResponsiveGridLayout>
              {/* Extra space at bottom to allow for easier card resizing */}
              <div className="h-16"></div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

// Add display names for better debugging
DashboardTabContent.displayName = 'DashboardTabContent';

interface DashboardTabManagerProps {
  tabs: DashboardTab[];
  activeTabId: string;
  tabStates: Record<string, TabState>;
  onStateChange: (tabId: string, updates: Partial<TabState>) => void;
}

const DashboardTabManager: React.FC<DashboardTabManagerProps> = memo(
  ({ tabs, activeTabId, tabStates, onStateChange }) => {
    return (
      <div className="relative h-full w-full">
        {tabs.map((tab) => {
          const tabState = tabStates[tab.id];
          if (!tabState) return null;

          return (
            <MarketAggregatesProvider
              key={tab.id}
              initialState={{
                weightType: getWeightTypeForVariable(tabState.selectedVariable),
                ...tabState.contextState,
              }}
            >
              <DashboardTabContent
                tabId={tab.id}
                tabState={tabState}
                tab={tab}
                isActive={tab.id === activeTabId}
                onStateChange={onStateChange}
              />
            </MarketAggregatesProvider>
          );
        })}
      </div>
    );
  },
);

DashboardTabManager.displayName = 'DashboardTabManager';

export default DashboardTabManager;
